/**
 * ImageUploadField Component
 *
 * Reusable image upload field for BlockNote custom blocks
 * Handles file selection, upload progress, error handling, and preview display
 *
 * Features:
 * - File selection via click or drag-and-drop
 * - Image preview with loading states
 * - Upload progress indicator
 * - Error handling with user feedback
 * - File size and type validation
 * - Integration with /blocknote_images endpoint
 * - CSRF token handling
 *
 * Props:
 * - currentImageUrl: URL of currently uploaded image (for preview)
 * - onImageUploaded: Callback when upload completes (url, variants)
 * - blockType: Type of block for backend processing
 * - accept: Accepted file types (default: images)
 * - maxSize: Maximum file size in bytes (default: 5MB)
 *
 * Usage:
 *   <ImageUploadField
 *     currentImageUrl={imageUrl}
 *     onImageUploaded={(url, variants) => handleImageUpdate(url)}
 *     blockType="productCardSimple"
 *   />
 */

import React, { useState, useRef, useEffect } from "react";

export default function ImageUploadField({
  currentImageUrl,
  onImageUploaded,
  blockType,
  blockId = null, // Unique block ID to identify which instance should receive the image
  accept = "image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif",
  maxSize = 5 * 1024 * 1024, // 5MB default
  hasError = false,
  onTouched = null,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const fileInputRef = useRef(null);

  // Listen for media browser selection events
  useEffect(() => {
    const handleMediaBrowserSelected = (event) => {
      // Only handle events for this specific block instance
      // Check both blockType AND blockId to ensure uniqueness
      if (
        event.detail.blockType === blockType &&
        event.detail.blockId === blockId
      ) {
        const { imageUrl, variants, blob } = event.detail;
        setPreviewUrl(imageUrl);
        if (onImageUploaded) {
          onImageUploaded(imageUrl, variants || {}, blob || null);
        }
      }
    };

    document.addEventListener(
      "mediaBrowserSelected",
      handleMediaBrowserSelected,
    );

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener(
        "mediaBrowserSelected",
        handleMediaBrowserSelected,
      );
    };
  }, [blockType, blockId, onImageUploaded]); // Dependencies to avoid stale closures

  const handleFileSelect = (e) => {
    if (typeof onTouched === "function") onTouched();
    const file = e.target.files?.[0];
    if (file) {
      validateAndUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onTouched === "function") onTouched();

    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndUpload(file);
    }
  };

  const validateAndUpload = async (file) => {
    setError(null);

    // Validate file type
    const acceptedTypes = accept.split(",").map((t) => t.trim());
    if (!acceptedTypes.some((type) => file.type.match(type))) {
      setError("Invalid file type. Please select a valid image file.");
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      const sizeMB = (maxSize / 1024 / 1024).toFixed(0);
      setError(`File too large. Maximum size is ${sizeMB}MB.`);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("block_type", blockType);

    try {
      // Get CSRF token
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
      if (!csrfToken) {
        throw new Error("CSRF token not found");
      }

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      // Create promise for async/await
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error("Invalid server response"));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || "Upload failed"));
            } catch (e) {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Network error during upload"));
        });

        xhr.addEventListener("abort", () => {
          reject(new Error("Upload cancelled"));
        });
      });

      // Send request
      xhr.open("POST", "/blocknote_images");
      xhr.setRequestHeader("X-CSRF-Token", csrfToken);
      xhr.send(formData);

      // Wait for response
      const response = await uploadPromise;

      if (response.success && response.url) {
        // Update preview with final URL
        const finalUrl =
          response.variants?.["500px"] ||
          response.variants?.["200px"] ||
          response.url;
        setPreviewUrl(finalUrl);

        // Notify parent component
        if (onImageUploaded) {
          onImageUploaded(response.url, response.variants, response.blob);
        }

        setUploadProgress(100);
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
      } else {
        throw new Error(response.error || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Upload failed. Please try again.");
      setIsUploading(false);
      setUploadProgress(0);
      setPreviewUrl(currentImageUrl); // Reset to original
    }
  };

  const handleClick = () => {
    if (typeof onTouched === "function") onTouched();
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    if (onImageUploaded) {
      onImageUploaded("", {}, null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBrowseLibrary = () => {
    if (typeof onTouched === "function") onTouched();
    // Dispatch event to request media browser modal
    document.dispatchEvent(
      new CustomEvent("mediaBrowserRequested", {
        detail: {
          blockType,
          blockId, // Pass unique block ID to identify this specific instance
        },
      }),
    );
  };

  const getFileName = () => {
    if (isUploading) return `Uploading... ${uploadProgress}%`;
    if (previewUrl) return "Image uploaded";
    return "No file chosen";
  };

  return (
    <div
      className={`image-upload-field mb-6 relative gap-y-1.5${hasError ? " error" : ""}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="sr-only"
        id={`image-upload-field-${blockType}-${blockId}`}
      />

      {previewUrl && (
        // Image preview
        <div className="mb-3 relative">
          <img
            src={previewUrl}
            alt="Upload preview"
            className="w-full max-w-md h-48 object-contain rounded-lg border border-gray-300"
          />
          {!isUploading && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
              aria-label="Remove image"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
              <div className="bg-white rounded-lg p-4">
                <div className="text-center">
                  <div className="text-sm font-medium mb-2">Uploading...</div>
                  <div className="w-48 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {uploadProgress}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Only show upload and browse buttons if no image is present */}
      {!previewUrl && (
        <div className="flex gap-2 items-center">
          {/* Unified Upload Button */}
          <div className="relative grow">
            <label
              htmlFor={`image-upload-field-${blockType}-${blockId}`}
              className={`flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-100 focus-within:ring-2 focus-within:ring-neutral-600 focus-within:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-700${hasError ? " error" : ""}`}
              onClick={handleClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <span className="shrink-0">Choose File</span>
              <span className="truncate text-neutral-500 dark:text-neutral-400 text-xs">
                {getFileName()}
              </span>
            </label>
          </div>

          {/* Browse Library Button */}
          <div className="">
            <button
              type="button"
              onClick={handleBrowseLibrary}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-neutral-200 dark:border-neutral-600 dark:hover:bg-neutral-600"
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Browse Library
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
}
